/** An error of amazon-cognito-identity-js: `code` (or `name`) is Cognito's exception. */
export type CognitoError = { code?: string; name?: string; message?: string };

const errorCode = (err: CognitoError | null | undefined) =>
  err?.code || err?.name || '';

/**
 * The message of a failed sign-in. A wrong password, an unknown or a disabled
 * account and the lockout after too many attempts read the same, so the form
 * does not tell whether an account exists (2023 pen test, finding 01; the app
 * client also has prevent_user_existence_errors).
 */
export function loginErrorMessage(err: CognitoError | null | undefined) {
  switch (errorCode(err)) {
    case 'NotAuthorizedException':
    case 'UserNotFoundException':
      return 'Incorrect e-mail or password. After several failed attempts, wait a few minutes before you try again.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
      return 'Too many attempts. Please wait a few minutes and try again.';
    default:
      return err?.message || 'Sign-in failed.';
  }
}

/**
 * The message of a failed password change (Cognito ChangePassword) for the
 * form; the password rules are those of the user pool.
 */
export function changePasswordErrorMessage(
  err: CognitoError | null | undefined
) {
  switch (errorCode(err)) {
    case 'NotAuthorizedException':
      return 'The current password is not correct.';
    case 'InvalidPasswordException':
    case 'InvalidParameterException':
      return `The new password does not meet the rules. ${err?.message ?? ''}`.trim();
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return 'Too many attempts. Please wait a few minutes and try again.';
    default:
      return err?.message || 'The password could not be changed.';
  }
}

/** The password rules of the Cognito user pool. */
export const passwordMinLength = 10;

export type PasswordRule = {
  id: string;
  label: string;
  test: (p: string) => boolean;
};

export const passwordRules: PasswordRule[] = [
  {
    id: 'length',
    label: `At least ${passwordMinLength} characters`,
    test: (p) => p.length >= passwordMinLength,
  },
  { id: 'lower', label: 'A lowercase letter', test: (p) => /[a-z]/.test(p) },
  { id: 'upper', label: 'An uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { id: 'number', label: 'A number', test: (p) => /[0-9]/.test(p) },
  {
    id: 'symbol',
    label: 'A special character',
    // Cognito's special characters, plus the space.
    test: (p) => /[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+\- ]/.test(p),
  },
];
